import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, CalendarDays, Clock, X, ArrowRight, ChevronLeft } from "lucide-react";
import BrochureProvider from "../components/BrochureProvider";
import Header from "../components/Header";
import Footer from "../components/Footer";
import WhatsAppButton from "../components/WhatsAppButton";
import ChatWidget from "../components/ChatWidget";
import { blog } from "../mock";
import useReveal from "../hooks/useReveal";

function ArticleReader({ article, onClose, onNext }) {
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => (document.body.style.overflow = "");
  }, []);

  return (
    <div className="fixed inset-0 z-[110] bg-white overflow-y-auto">
      <div className="relative h-[42vh] min-h-[300px] w-full">
        <img src={article.image} alt={article.title} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0d1640]/90 via-[#0d1640]/30 to-[#0d1640]/40" />
        <button onClick={onClose} className="absolute top-5 left-5 kse-btn bg-white/90 text-[#1a2a6b] px-4 py-2 text-sm">
          <ChevronLeft size={18} /> Back to Insights
        </button>
        <button onClick={onClose} className="absolute top-5 right-5 w-10 h-10 rounded-full bg-white/90 text-[#1a2a6b] flex items-center justify-center">
          <X size={20} />
        </button>
        <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-10">
          <div className="kse-container px-0">
            <span className="inline-block bg-[#2b3aa0] text-white text-xs font-bold px-3 py-1.5 rounded-full mb-4">
              {article.category}
            </span>
            <h1 className="text-white font-extrabold text-2xl sm:text-4xl max-w-3xl leading-tight">
              {article.title}
            </h1>
            <div className="flex items-center gap-5 text-blue-100/80 text-sm mt-4">
              <span className="flex items-center gap-1.5"><CalendarDays size={15} /> {article.date}</span>
              <span className="flex items-center gap-1.5"><Clock size={15} /> {article.readTime}</span>
            </div>
          </div>
        </div>
      </div>

      <article className="max-w-3xl mx-auto px-6 py-12">
        <p className="text-xl text-slate-600 leading-relaxed mb-8 font-medium">{article.excerpt}</p>
        {article.body.map((para, i) => (
          <p key={i} className="text-slate-700 leading-[1.9] mb-6 text-[17px]">{para}</p>
        ))}

        <div className="border-t border-slate-100 mt-10 pt-8">
          <button onClick={onNext} className="kse-btn bg-[#2b3aa0] hover:bg-[#1a2a6b] text-white px-6 py-3">
            Read Next Article <ArrowRight size={18} />
          </button>
        </div>
      </article>
    </div>
  );
}

export default function Blog() {
  const navigate = useNavigate();
  const [openIdx, setOpenIdx] = useState(null);
  const [ref, inView] = useReveal();

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const [featured, ...rest] = blog.articles;

  return (
    <BrochureProvider>
      <Header />

      {/* Hero */}
      <section className="relative pt-32 pb-16 bg-[#111c4e] overflow-hidden">
        <div className="absolute -right-32 top-10 w-96 h-96 rounded-full bg-[#2b3aa0]/30 blur-3xl" />
        <div className="kse-container relative z-10 text-center">
          <button onClick={() => navigate("/")} className="kse-btn text-white border border-white/30 px-5 py-2 text-sm mb-8 mx-auto hover:bg-white/10">
            <ArrowLeft size={16} /> Back to Home
          </button>
          <span className="inline-block bg-white text-[#1a2a6b] text-xs font-bold tracking-[0.2em] px-4 py-1.5 rounded-full mb-5">
            {blog.eyebrow}
          </span>
          <h1 className="text-white font-extrabold text-4xl sm:text-6xl leading-tight max-w-3xl mx-auto">
            {blog.title}
          </h1>
          <p className="text-blue-100/75 mt-5 text-lg max-w-2xl mx-auto">{blog.subtitle}</p>
        </div>
      </section>

      <div className="kse-container py-16">
        {/* Featured */}
        <button
          onClick={() => setOpenIdx(0)}
          className="group grid lg:grid-cols-2 gap-8 items-center text-left w-full mb-16 bg-slate-50 rounded-3xl overflow-hidden hover:shadow-2xl transition-all"
        >
          <div className="h-64 lg:h-full overflow-hidden">
            <img src={featured.image} alt={featured.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
          </div>
          <div className="p-8 lg:pr-12">
            <span className="inline-block bg-[#2b3aa0] text-white text-xs font-bold px-3 py-1.5 rounded-full mb-4">
              Featured &middot; {featured.category}
            </span>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-[#1a2a6b] leading-tight group-hover:text-[#2b3aa0] transition-colors">
              {featured.title}
            </h2>
            <p className="text-slate-500 mt-4 leading-relaxed">{featured.excerpt}</p>
            <div className="flex items-center gap-5 text-slate-400 text-sm mt-5">
              <span className="flex items-center gap-1.5"><CalendarDays size={15} /> {featured.date}</span>
              <span className="flex items-center gap-1.5"><Clock size={15} /> {featured.readTime}</span>
            </div>
            <span className="inline-flex items-center gap-2 text-[#2b3aa0] font-semibold mt-6">
              Read Article <ArrowRight size={18} />
            </span>
          </div>
        </button>

        {/* Grid */}
        <div ref={ref} className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {rest.map((a, i) => (
            <button
              key={a.id}
              onClick={() => setOpenIdx(i + 1)}
              className={`group text-left bg-white rounded-3xl overflow-hidden border border-slate-100 shadow-sm hover:shadow-2xl transition-all reveal ${inView ? "in-view" : ""}`}
              style={{ animationDelay: `${(i % 3) * 0.1}s` }}
            >
              <div className="h-52 overflow-hidden relative">
                <img src={a.image} alt={a.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                <span className="absolute top-3 left-3 bg-white/90 backdrop-blur text-[#1a2a6b] text-[11px] font-bold px-3 py-1 rounded-full">
                  {a.category}
                </span>
              </div>
              <div className="p-6">
                <h3 className="text-lg font-bold text-[#1a2a6b] leading-snug group-hover:text-[#2b3aa0] transition-colors">
                  {a.title}
                </h3>
                <p className="text-slate-500 text-sm mt-2 line-clamp-2">{a.excerpt}</p>
                <div className="flex items-center gap-4 text-slate-400 text-xs mt-4">
                  <span className="flex items-center gap-1.5"><CalendarDays size={13} /> {a.date}</span>
                  <span className="flex items-center gap-1.5"><Clock size={13} /> {a.readTime}</span>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      <Footer />
      <WhatsAppButton />
      <ChatWidget />

      {openIdx !== null && (
        <ArticleReader
          article={blog.articles[openIdx]}
          onClose={() => setOpenIdx(null)}
          onNext={() => setOpenIdx((openIdx + 1) % blog.articles.length)}
        />
      )}
    </BrochureProvider>
  );
}
